import { useQuery } from '@tanstack/react-query';

import {
  contractualTerms,
  type ContractualTerm,
  type ContractualTerms,
  type OperationDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';

import { permissoesDaCotacao } from './cotacao';

/**
 * Apoio dos termos contratuais da cotacao (P-COT-2): o cache unico das duas secoes do
 * Resumo (clausulas e Objetos) e o que a pessoa pode fazer com eles.
 */

/** A chave do cache dos termos da cotacao, a mesma para as duas secoes do Resumo. */
export function chaveDosTermos(operationId: string) {
  return ['operations', operationId, 'contractual-terms'] as const;
}

/** Os termos da cotacao, lidos uma vez para as clausulas e para o editor. */
export function useTermosDaCotacao(operationId: string) {
  return useQuery({
    queryKey: chaveDosTermos(operationId),
    queryFn: ({ signal }) => contractualTerms.get(operationId, signal),
  });
}

/**
 * O que a pessoa pode fazer com os termos: a permissao fina e a etapa. Quem decide e o
 * servidor; a tela so desabilita o que ele recusaria.
 *
 * A corretora edita o objeto, as TAGs e as clausulas que o cadastro deixa ela escolher
 * na propria cotacao ainda em em_cotacao (`operacoes.cotacao.update_own`, P-NCT).
 * Carregar os textos da modalidade e a minuta condicionada continuam com a seguradora
 * (`montar`).
 */
export function usePermissoesDosTermos(
  operacao: OperationDetail,
  termos: ContractualTerms | undefined,
) {
  const { can } = useAuth();
  const aberta =
    !operacao.is_terminal &&
    termos?.editable === true &&
    can('operacoes.operacao.update');
  const propria =
    operacao.stage === 'em_cotacao' &&
    termos?.editable === true &&
    operacao.quote_actions?.can_edit === true &&
    can('operacoes.cotacao.update_own');
  const daSeguradora = aberta && can(permissoesDaCotacao.textos);
  const textos = daSeguradora || propria;
  // Sem a permissao da seguradora, a edicao e a da corretora: so o que o OnPoint deixa
  // a corretora mudar (achado A2 da revisao das ondas 5 e 6).
  const soDaCorretora = propria && !daSeguradora;
  return {
    textos,
    clausulas: (aberta && can(permissoesDaCotacao.clausulas)) || propria,
    montar: aberta && can(permissoesDaCotacao.textos),
    soDaCorretora,
    doBloco: (termo: ContractualTerm) =>
      permissoesDoBloco(termo, textos, soDaCorretora),
  };
}

/** O que a pessoa muda num bloco dos termos: o texto, o "Habilitar na cotacao" e as TAGs. */
export interface PermissoesDoBloco {
  texto: boolean;
  habilitar: boolean;
  tags: boolean;
}

/**
 * O que muda em cada bloco. A seguradora muda tudo (o texto da clausula so na clausula
 * em branco, que o servidor confere). A corretora, na propria cotacao, muda o que o
 * OnPoint deixa: o texto e as TAGs do objeto (NCT-133, NCT-138 e NCT-139), as TAGs das
 * clausulas e, na clausula que o cadastro deixa ela escolher, o texto e a habilitacao
 * (NCT-143 e NCT-144). Condicoes gerais e especiais e a habilitacao do objeto sao da
 * seguradora. O servidor decide; a tela so desabilita o que ele recusaria.
 */
export function permissoesDoBloco(
  termo: ContractualTerm,
  podeEditar: boolean,
  soDaCorretora: boolean,
): PermissoesDoBloco {
  if (!podeEditar) return { texto: false, habilitar: false, tags: false };
  if (!soDaCorretora) return { texto: true, habilitar: true, tags: true };
  if (
    termo.kind === 'objeto_modalidade' ||
    termo.kind === 'objeto_complementar' ||
    termo.kind === 'objeto_cobertura'
  ) {
    return { texto: true, habilitar: false, tags: true };
  }
  if (termo.kind === 'clausula_particular') {
    const escolhivel = termo.particular_clause?.broker_can_select === true;
    return { texto: escolhivel, habilitar: escolhivel, tags: true };
  }
  return { texto: false, habilitar: false, tags: false };
}

/** Aviso do OnPoint ao avancar do passo Objeto com TAG sem valor (NCT-133). */
export const AVISO_TAGS_NAO_PREENCHIDAS = 'Existem tags obrigatorias nao preenchidas';

/**
 * Diz se algum bloco habilitado do editor "Objetos" tem TAG sem valor gravado (NCT-133).
 *
 * Toda TAG do bloco habilitado conta: o SeguraCore nao marca TAG como obrigatoria, e a
 * lista de marcadores por modalidade esta a confirmar (CPO-280). O bloco desligado nao
 * sai na minuta e nao conta. So avisa, sem travar o envio: se o aviso deve travar esta
 * em docs/OPEN_QUESTIONS.md, secao 2.49.
 */
export function temTagVaziaNoBlocoHabilitado(
  termos: ContractualTerms | undefined,
): boolean {
  return (termos?.items ?? []).some(
    (termo) =>
      termo.kind !== 'clausula_particular' &&
      termo.enabled &&
      termo.tags.some((tag) => (termo.tag_values[tag.tag_key] ?? '').trim() === ''),
  );
}
